import { useMemo } from "react";

export interface MiniWaveformProps {
  ariaLabel?: string;
  barCount?: number;
  className?: string;
  peaks: number[];
}

const DEFAULT_BAR_COUNT = 48;

// SVG user-space height; bars mirror around the vertical midline (50).
const VIEWBOX_HEIGHT = 100;

// Floor height (in viewBox units) so silence still reads as a hairline glyph
// rather than vanishing from the row.
const MIN_BAR = 2;

// Fraction of each 1-unit bar cell left empty, split as leading/trailing
// gutters so adjacent bars stay visually separated.
const BAR_GAP = 0.3;

interface MiniWaveformBar {
  height: number;
  width: number;
  x: number;
  y: number;
}

function clampPeak(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

// Bucket-max downsample: each output bar takes the loudest peak in its slice so
// transients survive the reduction to `barCount` bars. Inputs at or under the
// budget keep one bar per peak.
function downsamplePeaks(peaks: number[], barCount: number): number[] {
  if (peaks.length <= barCount) {
    return peaks.map(clampPeak);
  }

  const buckets: number[] = [];
  for (let index = 0; index < barCount; index += 1) {
    const start = Math.floor((index * peaks.length) / barCount);
    const end = Math.floor(((index + 1) * peaks.length) / barCount);
    let peak = 0;
    for (let cursor = start; cursor < end; cursor += 1) {
      peak = Math.max(peak, clampPeak(peaks[cursor] ?? 0));
    }
    buckets.push(peak);
  }
  return buckets;
}

function waveformBars(peaks: number[], barCount: number): MiniWaveformBar[] {
  return downsamplePeaks(peaks, barCount).map((peak, index) => {
    const height = Math.max(MIN_BAR, peak * VIEWBOX_HEIGHT);
    return {
      height,
      width: 1 - BAR_GAP,
      x: index + BAR_GAP / 2,
      y: (VIEWBOX_HEIGHT - height) / 2,
    };
  });
}

/**
 * Compact, non-interactive waveform glyph for dense/virtualized rows: pure
 * geometry, no audio element, no state or effects, so it stays cheap across
 * many rows. Feed it precomputed peaks (0..1); it buckets them down to
 * `barCount` bars centered on the midline.
 */
export function MiniWaveform({
  ariaLabel,
  barCount = DEFAULT_BAR_COUNT,
  className,
  peaks,
}: MiniWaveformProps) {
  const bars = useMemo(() => waveformBars(peaks, barCount), [barCount, peaks]);

  const classes = ["marker-mini-waveform"];
  if (className) {
    classes.push(className);
  }

  return (
    <svg
      aria-hidden={ariaLabel ? undefined : "true"}
      aria-label={ariaLabel}
      className={classes.join(" ")}
      focusable="false"
      preserveAspectRatio="none"
      role={ariaLabel ? "img" : undefined}
      viewBox={`0 0 ${Math.max(1, bars.length).toString()} ${VIEWBOX_HEIGHT.toString()}`}
    >
      {bars.map((bar, index) => (
        <rect
          className="marker-mini-waveform__bar"
          height={bar.height}
          key={index}
          width={bar.width}
          x={bar.x}
          y={bar.y}
        />
      ))}
    </svg>
  );
}
